/* eslint-disable react/no-multi-comp */
import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import ApolloClient from 'apollo-client';
import { useQuery, QueryHookOptions } from '@apollo/react-hooks';
import styled from '@emotion/styled';
import { MuiButton } from '@onehope/design-system-ohw';
import GET_SEGMENT_IDENTIFY_QUERY from './SegmentIdentify';
import { urlHomepageBackground } from '../../../../../server/utils/urlConstants';

function getMaxWidth(textWrapperImage: string, isTextBoxOverlay: boolean) {
  if (isTextBoxOverlay) {
    return '55%';
  }
  if (textWrapperImage) {
    return '600px';
  }
  return '500px';
}

declare global {
  interface Window {
    analytics: any;
  }
}

interface ImageTileProps {
  alt?: string;
  mobile: {
    src: string;
    height: number;
    width: number;
  };
  tablet: {
    src: string;
    height: number;
    width: number;
  };
  desktop: {
    src: string;
    height: number;
    width: number;
  };
}

interface TastingKitPromoComponentProps {
  user: any;
  apolloClient: ApolloClient<any>;
  config: {
    buttonLink?: string;
    buttonTwoLink?: string | null;
    images: ImageTileProps;
    imageSide: string;
    isTextBoxOverlay?: true;
    textAlign: string;
    headerTextAlign?: string;
    textWrapperImage?: {
      alt: string;
      src: string;
      height: number;
      width: number;
    };
    buttonOverlayLink?: string;
  };
}

const ButtonStyles = {
  maxWidth: 288,
};

const ImageTile = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
`;

const ImageWrapper = styled.div<ImageTileProps>`
  position: relative;
  height: 0;
  padding-top: ${({ mobile }) => `${(mobile.height / mobile.width) * 100}%`};
  @media screen and (min-width: 768px) {
    padding-top: ${({ tablet }) => `${(tablet.height / tablet.width) * 100}%`};
  }
  @media screen and (min-width: 1440px) {
    padding-top: ${({ desktop }) =>
      `${(desktop.height / desktop.width) * 100}%`};
  }
`;

const Container = styled.div<any>`
  display: flex;
  position: relative;
  flex-direction: column;
  @media screen and (min-width: 768px) {
    flex-wrap: wrap;
    flex-direction: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? 'row-reverse' : 'column'};
  }
  @media screen and (min-width: 1440px) {
    flex-wrap: wrap;
    flex-direction: ${({ imageSide }) =>
      imageSide === 'left' ? 'row' : 'row-reverse'};
  }
`;

const ImageContainer = styled.div<any>`
  position: relative;
  @media screen and (min-width: 768px) {
    width: ${({ isTextBoxOverlay }) => (isTextBoxOverlay ? '50%' : '100%')};
  }
  @media screen and (min-width: 1440px) {
    width: ${({ isTextBoxOverlay }) => (isTextBoxOverlay ? '55%' : '50%')};
  }
`;

const TextBoxWrapper = styled.div<any>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  color: #0a0a0a;
  font-family: 'Gotham A', 'Gotham B', sans-serif;
  /* font-family: 'Playfair Display', 'proxima-nova', serif; */
  text-align: center;
  margin: 0 auto;
  padding: 40px 0;
  max-width: ${({ textWrapperImage }) =>
    textWrapperImage ? '600px' : '500px'};
  @media screen and (min-width: 768px) {
    text-align: ${({ textAlign }) => textAlign};
    width: ${({ isTextBoxOverlay }) => (isTextBoxOverlay ? '50%' : '100%')};
  }
  @media screen and (min-width: 1440px) {
    padding: 0;
    right: 47%;
    top: 50%;
    background-color: white;
    transform: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? ' translateY(-50%)' : 'none'};
    width: ${({ isTextBoxOverlay }) => (isTextBoxOverlay ? '740px' : '50%')};
    max-width: ${({ isTextBoxOverlay, textWrapperImage }) =>
      getMaxWidth(textWrapperImage, isTextBoxOverlay)};
    height: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? '640px' : 'inherit'};
    position: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? 'absolute' : 'unset'};
    box-shadow: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? '0 6px 20px 0 rgba(0, 0, 0, 0.15)' : 'none'};
  }
`;

const TextBoxContainer = styled.div<any>`
  padding: 16px;
  text-align: center;
  @media screen and (min-width: 768px) {
    text-align: ${({ textWrapperImage, textAlign, isTextBoxOverlay }) =>
      isTextBoxOverlay || textWrapperImage ? 'left' : textAlign};
    padding: ${({ textAlign }) =>
      textAlign === 'left' ? '16px 48px' : '16px'};
  }
  @media screen and (min-width: 1440px) {
    text-align: ${({ headerTextAlign }) => headerTextAlign || 'left'};
    width: 500px;
  }
`;

const Header = styled.div`
  font-size: 32px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.31;
  letter-spacing: -0.5px;
  font-family: 'Playfair Display', 'proxima-nova', serif;
  @media screen and (min-width: 1440px) {
    font-size: 44px;
    line-height: 1.32;
  }
`;

const MiniHeader = styled.div`
  font-size: 10px;
  font-weight: 500;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.6;
  letter-spacing: 2px;
  padding-bottom: 24px;
  text-transform: uppercase;
`;

const Summary = styled.div<any>`
  padding: 24px 0;
  font-size: 16px;
  font-weight: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  @media screen and (min-width: 768px) {
    max-width: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? '288px' : 'inherit'};
  }
  @media screen and (min-width: 1440px) {
    max-width: ${({ isTextBoxOverlay }) =>
      isTextBoxOverlay ? '432px' : 'inherit'};
  }
`;

function ResponsiveImage({ desktop, mobile, tablet, alt }: ImageTileProps) {
  return (
    <ImageWrapper mobile={mobile} tablet={tablet} desktop={desktop}>
      <picture>
        <source srcSet={desktop.src} media="(min-width: 1440px)" />
        <source srcSet={tablet.src} media="(min-width: 768px)" />
        <ImageTile srcSet={mobile.src} alt={alt} />
      </picture>
    </ImageWrapper>
  );
}

function TastingKitPromoComponent({
  apolloClient,
  user,
  config,
}: TastingKitPromoComponentProps) {
  const [anonymousId, setAnonymousId] = useState('');

  // load segment anonymousId after window load
  useEffect(() => {
    if (window && window.analytics) {
      if (typeof window.analytics.user !== 'function') return;
      const returnedId = window.analytics.user().anonymousId();
      setAnonymousId(returnedId);
    }
  }, []);

  const queryOptions = {
    client: apolloClient,
    variables: { anonymousId, accountId: user.userId },
    skip: !user.userId && !anonymousId,
  } as QueryHookOptions;

  // query segmentIdentify for segment information
  const { data } = useQuery(GET_SEGMENT_IDENTIFY_QUERY, queryOptions);
  const codeExpiresDate =
    user &&
    user.availableTastingFlightCode &&
    user.availableTastingFlightCode.expiresOn;

  const TEXT_CONFIG = {
    tastingKit: {
      miniHeader: 'Try Our Tasting Flights',
      header: 'A Taste of Napa',
      summary: `Transport yourself to wine country through ONEHOPE’s new tasting flights! Try four of our best-selling wines for just $29, and enjoy the tasting room experience from the comfort of your home. Plus, we’ll give you a credit on your next 4+ bottle purchase, essentially waiving your tasting fee!`,
      buttonText: 'Shop Now',
      buttonLink: '/tasting-flight',
      images: {
        alt:
          'Try four of our best-selling wines for just $29, from the comfort of your own home.',
        desktop: {
          src: `${urlHomepageBackground}TastingFlightPromo_Desktop.jpg`,
          height: 1920,
          width: 2160,
        },
        tablet: {
          src: `${urlHomepageBackground}TastingFlightPromo_Tablet.jpg`,
          height: 1152,
          width: 2304,
        },
        mobile: {
          src: `${urlHomepageBackground}TastingFlightPromo_Mobile.jpg`,
          height: 720,
          width: 960,
        },
      },
    },
    purchaseWine: {
      miniHeader: 'Love Your Tasting',
      header: 'Buy 4+ bottles and we’ll waive your flight!',
      summary: `Thank you for sampling our award-winning wines! A $15 wine credit has been added to your account and can be used through ${DateTime.fromISO(
        codeExpiresDate,
      ).toLocaleString(
        DateTime.DATE_MED,
      )} on your next purchase of four bottles or more. When you combine your credit with our pack discounts, the cost of your tasting flight is essentially free!`,
      buttonText: 'Shop Now',
      buttonLink: '/shop',
      images: {
        alt:
          'Try four of our best-selling wines for $29, from the comfort of your own home.',
        desktop: {
          src: `${urlHomepageBackground}TastingFlightBottles_Desktop.jpg`,
          height: 1920,
          width: 2160,
        },
        tablet: {
          src: `${urlHomepageBackground}TastingFlightBottles_Tablet.jpg`,
          height: 1152,
          width: 2304,
        },
        mobile: {
          src: `${urlHomepageBackground}TastingFlightBottles_Mobile.jpg`,
          height: 720,
          width: 960,
        },
      },
    },
    rewards: {
      miniHeader: 'Earn rewards with every purchase',
      header: 'Shop, Sip and Save!',
      summary: `As a thank you for helping us nourish the future, you’ll earn reward points every time you shop with us. All customers receive 5% back in rewards, or you can double your savings to 10% as a Wine Club member or subscriber! Use your rewards for discounts on wine, or donate them to a ONEHOPE featured nonprofit.`,
      buttonText: 'Shop Now',
      buttonLink: '/shop',
      images: {
        alt:
          'Try four of our best-selling wines for $29, from the comfort of your own home.',
        desktop: {
          src: `${urlHomepageBackground}TastingFlightRewards_Desktop.jpg`,
          height: 1920,
          width: 2160,
        },
        tablet: {
          src: `${urlHomepageBackground}TastingFlightRewards_Tablet.jpg`,
          height: 1152,
          width: 2304,
        },
        mobile: {
          src: `${urlHomepageBackground}TastingFlightRewards_Mobile.jpg`,
          height: 720,
          width: 960,
        },
      },
    },
  };

  const calculateRender = () => {
    const segmentPersonas = data && data.viewer.segmentIdentify;
    const availableTKCode = user && user.availableTastingFlightCode;
    const userRole = user && user.role;
    // if promo code available show purchae wine reminder
    if (availableTKCode && availableTKCode.code) {
      return TEXT_CONFIG['purchaseWine'];
    }
    // if segmentIdentify retruns prospect is true, show TK
    if (segmentPersonas && segmentPersonas.prospects === true) {
      return TEXT_CONFIG['tastingKit'];
    }
    // if user is all_customers show rewards
    if (segmentPersonas && segmentPersonas.allCustomers === true) {
      return TEXT_CONFIG['rewards'];
    }
    // if user is logged out, show tasting kit
    if (userRole === 'anonymous') {
      return TEXT_CONFIG['tastingKit'];
    }
    // otherwise show rewards reminder
    return TEXT_CONFIG['rewards'];
  };

  const currentTextConfig = calculateRender();
  const {
    images: { mobile, tablet, desktop, alt },
    header,
    miniHeader,
    summary,
  } = currentTextConfig;

  const onButtonClickOne = () => {
    const { buttonLink } = currentTextConfig;
    window.location.href = buttonLink;
  };

  const {
    imageSide,
    textAlign,
    isTextBoxOverlay,
    textWrapperImage,
    headerTextAlign,
  } = config;

  return (
    <Container imageSide={imageSide} isTextBoxOverlay={isTextBoxOverlay}>
      <ImageContainer isTextBoxOverlay={isTextBoxOverlay}>
        <ResponsiveImage
          alt={alt}
          mobile={mobile}
          tablet={tablet}
          desktop={desktop}
        />
      </ImageContainer>
      <TextBoxWrapper
        textAlign={textAlign}
        textWrapperImage={textWrapperImage}
        isTextBoxOverlay={isTextBoxOverlay}
      >
        <TextBoxContainer
          textAlign={textAlign}
          headerTextAlign={headerTextAlign}
          textWrapperImage={textWrapperImage}
          isTextBoxOverlay={isTextBoxOverlay}
        >
          <MiniHeader>{miniHeader}</MiniHeader>
          <Header>{header}</Header>
          <Summary isTextBoxOverlay={isTextBoxOverlay}>{summary}</Summary>
          <MuiButton
            style={ButtonStyles}
            onClick={onButtonClickOne}
            type="button"
            fullWidth
          >
            {currentTextConfig.buttonText}
          </MuiButton>
        </TextBoxContainer>
      </TextBoxWrapper>
    </Container>
  );
}

export default TastingKitPromoComponent;
